import React from 'react';
import { CanvasNodeLayout } from '../../types/canvas';
import { CareerLevel } from '../../types/career';
import { formatAE } from '../../utils/formatters';

interface NodeCardProps {
  node: CanvasNodeLayout;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isDimmed?: boolean;
  onClick?: (nodeId: string) => void;
  onAddChild?: (parentId: string) => void;
}

const LEVEL_STYLES: Record<CareerLevel, { bg: string; border: string; text: string; glow: string }> = {
  1: { bg: '#1E293B', border: '#64748B', text: '#94A3B8', glow: 'rgba(100, 116, 139, 0.4)' },
  2: { bg: '#172554', border: '#3B82F6', text: '#60A5FA', glow: 'rgba(59, 130, 246, 0.5)' },
  3: { bg: '#083344', border: '#06B6D4', text: '#22D3EE', glow: 'rgba(6, 182, 212, 0.5)' },
  4: { bg: '#022C22', border: '#10B981', text: '#34D399', glow: 'rgba(16, 185, 129, 0.6)' },
  5: { bg: '#064E3B', border: '#00E599', text: '#00E599', glow: 'rgba(0, 229, 153, 0.7)' },
  6: { bg: '#451A03', border: '#F59E0B', text: '#FBBF24', glow: 'rgba(245, 158, 11, 0.6)' },
  7: { bg: '#500724', border: '#EC4899', text: '#F472B6', glow: 'rgba(236, 72, 153, 0.6)' },
  8: { bg: '#2E1065', border: '#8B5CF6', text: '#A78BFA', glow: 'rgba(139, 92, 246, 0.7)' },
};

export const NodeCard: React.FC<NodeCardProps> = ({
  node,
  isSelected = false,
  isHighlighted = false,
  isDimmed = false,
  onClick,
  onAddChild,
}) => {
  const { data, x, y, isRoot } = node;
  const levelStyle = LEVEL_STYLES[data.level] || LEVEL_STYLES[1];
  const isActive = data.isActive;

  // Circle Dimensions
  const radius = isRoot ? 28 : 23;

  // Stroke and selection styling
  let strokeColor = levelStyle.border;
  let strokeWidth = isRoot ? 2.8 : 2.2;
  let filter = isRoot ? 'url(#neon-glow-emerald)' : undefined;

  if (isSelected) {
    strokeColor = '#00E599';
    strokeWidth = 3.5;
    filter = 'url(#neon-glow-emerald)';
  } else if (isHighlighted) {
    strokeColor = '#06B6D4';
    strokeWidth = 3.5;
    filter = 'url(#neon-glow-cyan)';
  } else if (!isActive) {
    strokeColor = '#475569';
    strokeWidth = 1.6;
  }

  const opacity = isDimmed ? 0.2 : !isActive ? 0.65 : 1.0;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(node.id);
  };

  const handleAddChild = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddChild?.(node.id);
  };

  // Clean up redundant level markers inside name (e.g., "Олена (Менеджер 1 Р3)" -> "Олена (Менеджер 1)")
  const cleanName = isRoot
    ? 'Ви (Лідер)'
    : data.name
        .replace(/\s+[рpРP]\d+(?=\))/gi, '')
        .replace(/\([рpРP]\d+\s+([^)]+)\)/gi, '($1)')
        .replace(/\s*\([рpРP]\d+\)/gi, '')
        .replace(/\s+/g, ' ')
        .trim();

  // Allow comfortable name length without premature truncation
  const displayName = isRoot ? 'Ви (Лідер)' : cleanName.length > 22 ? `${cleanName.slice(0, 21)}…` : cleanName;

  // Comfortable pill width tailored to label text length (max 134px safely within 140px TreeLayout spacing)
  const pillWidth = Math.max(96, Math.min(134, Math.round(displayName.length * 6.2 + 14)));
  const pillX = -pillWidth / 2;

  return (
    <g
      data-tour="consultant-node"
      data-tour-root={isRoot ? "true" : "false"}
      data-node-id={node.id}
      transform={`translate(${x}, ${y})`}
      opacity={opacity}
      onClick={handleClick}
      className="cursor-pointer group select-none transition-all duration-200"
    >
      {/* Outer Pulse Ring for Root Leader */}
      {isRoot && (
        <circle
          r={radius + 7}
          fill="none"
          stroke="#10B981"
          strokeWidth="1"
          strokeOpacity="0.4"
          strokeDasharray="4 3"
        />
      )}

      {/* Main Node Bubble Circle */}
      <circle
        r={radius}
        fill={levelStyle.bg}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={!isActive ? '4 3' : undefined}
        filter={filter}
        className="group-hover:brightness-125 transition-all"
      />

      {/* Career Level Number in the Center */}
      <text
        y={isRoot ? '6.5' : '5.5'}
        textAnchor="middle"
        fontSize={isRoot ? '17' : '15'}
        fontWeight="900"
        fill={levelStyle.text}
        fontFamily="Inter, system-ui, sans-serif"
        className="pointer-events-none tracking-tight"
      >
        {`Р${data.level}`}
      </text>

      {/* Active / Inactive Status Dot indicator at top right of circle */}
      <circle
        cx={radius * 0.72}
        cy={-radius * 0.72}
        r={3.5}
        fill={isActive ? '#10B981' : '#64748B'}
        stroke="#0A0F1D"
        strokeWidth="1.2"
      />

      {/* Label Badge Container Under Circle */}
      <g transform={`translate(0, ${radius + 14})`}>
        {/* Name pill background */}
        <rect
          x={pillX}
          y="-9"
          width={pillWidth}
          height="18"
          rx="5"
          fill="#0B132B"
          fillOpacity="0.9"
          stroke="#1E293B"
          strokeWidth="0.8"
        />
        <text
          y="3.5"
          textAnchor="middle"
          fontSize="9.5"
          fontWeight="600"
          fill="#F8FAFC"
          fontFamily="Inter, system-ui, sans-serif"
          className="pointer-events-none"
        >
          {displayName}
        </text>

        {/* Monthly turnover small sub-pill */}
        <text
          y="18"
          textAnchor="middle"
          fontSize="8.5"
          fontWeight="700"
          fill={isActive ? '#00E599' : '#64748B'}
          fontFamily="Inter, system-ui, sans-serif"
          className="pointer-events-none font-mono"
        >
          {formatAE(data.totalMonthlyAE, 0)}
        </text>
      </g>

      {/* Quick (+) Add Subordinate Button Overlay on Hover/Selection */}
      {onAddChild && (
        <g
          transform={`translate(${radius + 2}, ${-radius - 2})`}
          onClick={handleAddChild}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 cursor-pointer"
          aria-label="Додати пряму лінію (+)"
        >
          <title>Додати пряму лінію (+)</title>
          <circle cx="0" cy="0" r="9" fill="#10B981" stroke="#0A0F1D" strokeWidth="1.5" />
          <text
            y="3.5"
            textAnchor="middle"
            fontSize="12"
            fontWeight="bold"
            fill="#0A0F1D"
            className="pointer-events-none"
          >
            +
          </text>
        </g>
      )}
    </g>
  );
};
